// ============================================================
// defence/chain - the defence chain's arithmetic.
//
// Opening the chain with the first reaction, advancing it one step
// (with Cleaving's blunted hits), and what `applyHits` is handed when it
// ends. No React; the only dice are `resolveDefenceStep`'s d100s.
// ============================================================
import type { UnnaturalDosMode } from '../../engine/unnaturalDos';
import {
  getAvailableDefenceSteps,
  resolveDefenceStep,
  hasCounterAttack,
  toLegacyDefenceResults,
  attackerDefenceMods,
} from '../../engine/defenceChain';
import type { DefenceStep, DefenceStepResult } from '../../engine/defenceChain';
import type { ConditionModOptions } from '../../engine/conditionModifiers';
import { resolveReactionAvailability } from '../../engine/reactionAvailability';
import { hasWS } from '../../engine/weaponSpecials';
import type { HitResult } from '../../engine/damageCalc';
import type { Target } from '../../types';
import type { DefenceChainState, PendingAttackData } from './types';

/** The table's rulings every step reads. */
export interface ChainRules {
  unnaturalDosMode: UnnaturalDosMode;
  applyConditionModifiers: boolean;
  condModOptions?: ConditionModOptions;
}

/** Which reads a first reaction takes, by kind. */
const FIRST_STEP = {
  dodge: {
    label: 'Dodge',
    charValue: (t: Target) => t.dodge.agility,
    multiplier: (t: Target) => t.unnaturalAgility || 1,
    mod: 'dodgeMod',
  },
  parry: {
    label: 'Parry',
    charValue: (t: Target) => t.parry.weaponSkill,
    multiplier: (t: Target) => t.unnaturalWeaponSkill || 1,
    mod: 'parryMod',
  },
} as const;

/**
 * The first reaction's step.
 *
 * THE FIRST REACTION IS BUILT HERE AND EVERY LATER ONE COMES FROM
 * `getAvailableDefenceSteps`, so anything this forgets applies to the
 * reaction people actually take and to no other. It forgot the condition
 * penalty once: a crippled combatant's prompt offered a Dodge at 15 and
 * rolled it at 35.
 */
function firstStep(choice: string, target: Target, attackerSpecials: string[], manualMod: number, rules: ChainRules) {
  const stepType = choice === 'dodge' ? 'dodge' : 'parry';
  const reads = FIRST_STEP[stepType];
  const stanceBonus = 0; // Ds is RAW now - see defenceChain (v8.216.0)
  const condMods = resolveReactionAvailability(target, {
    applyConditionMods: rules.applyConditionModifiers,
    condOptions: rules.condModOptions,
  });
  // The attacker's denial and reaction exposures (v8.364.3, v8.368.3) - the
  // same derivation the button and every later step use - and the banner's
  // table ruling (v8.366.0), this reaction and its chain only.
  const atk = attackerDefenceMods(attackerSpecials, target.parry?.parryWeaponSpecials, target.attackerReactionMods);
  const step: DefenceStep = {
    type: stepType,
    label: reads.label,
    skill: reads.charValue(target) + stanceBonus + condMods[reads.mod] + atk[reads.mod] + manualMod,
    ...(manualMod ? { manualMod } : {}),
    // Unnatural DoS inputs - MUST mirror getAvailableDefenceSteps. charValue
    // stays the RAW characteristic: Unnatural multiplies the characteristic.
    charValue: reads.charValue(target),
    multiplier: reads.multiplier(target),
  };
  return step;
}

/** Where the chain stands after a step: who defends, against what, with which steps taken. */
interface ChainPoint {
  target: Target;
  isMelee: boolean;
  completedSteps: DefenceStepResult[];
  remaining: number;
  attackerSpecials: string[];
  manualMod: number;
}

/** The steps still on offer - the one derivation every later reaction comes from. */
function offeredSteps(p: ChainPoint, rules: Omit<ChainRules, 'unnaturalDosMode'>): DefenceStep[] {
  return getAvailableDefenceSteps(
    p.target,
    p.isMelee,
    p.completedSteps,
    p.remaining,
    p.attackerSpecials,
    rules.applyConditionModifiers,
    rules.condModOptions,
    p.manualMod,
  );
}

/** Open the chain with the defender's first reaction, rolled. */
export function openChain(
  pa: PendingAttackData,
  target: Target,
  choice: string,
  manualMod: number,
  rules: ChainRules,
): DefenceChainState {
  const isMelee = pa.isMelee || false;
  const attackerSpecials = pa.slot?.specialRules || [];
  const step = firstStep(choice, target, attackerSpecials, manualMod, rules);
  const counter = target.counterAttackAvailable || false;
  const stepResult = resolveDefenceStep(step, pa.hits.length, counter, rules.unnaturalDosMode);
  const remainingHits = stepResult.avoided > 0 ? pa.hits.slice(stepResult.avoided) : [...pa.hits];
  const completedSteps = [stepResult];
  const point = { target, isMelee, completedSteps, remaining: remainingHits.length, attackerSpecials, manualMod };
  const availableSteps = offeredSteps(point, rules);
  return {
    completedSteps,
    remainingHits,
    availableSteps,
    counterAttackTriggered: hasCounterAttack(completedSteps),
    frozenTarget: target,
    isMelee,
    pendingAttackData: pa,
    tabId: pa.tabId,
    claimed: true,
    ...(manualMod ? { manualMod } : {}),
  };
}

/**
 * Cleaving (v8.287.0): a successful parry blunts the blow instead of
 * stopping it - the "avoided" hits come back at half damage, crits
 * stripped (the parry caught the killing edge), and cannot be re-avoided.
 */
function cleavedAfter(
  chain: DefenceChainState,
  stepResult: DefenceStepResult,
  attackerSpecials: string[],
): HitResult[] {
  const cleaved = chain.cleavedHits || [];
  const isParryStep = stepResult.step.type === 'parry' || stepResult.step.type === 'wall_of_steel_parry';
  if (!isParryStep || stepResult.avoided <= 0 || !hasWS(attackerSpecials, 'Cleaving')) return cleaved;
  const blunted = chain.remainingHits.slice(0, stepResult.avoided).map((h) => ({
    ...h,
    effectiveDamage: Math.floor((h.effectiveDamage || 0) / 2),
    crit: null,
    critSev: 0,
    righteousFury: false,
    rfConfirmed: false,
  }));
  return [...cleaved, ...blunted];
}

/** The chain after one more step: hits removed (or cleaved), the next steps offered. */
export function advanceChain(
  chain: DefenceChainState,
  stepResult: DefenceStepResult,
  rules: Omit<ChainRules, 'unnaturalDosMode'>,
): DefenceChainState {
  const completedSteps = [...chain.completedSteps, stepResult];
  const attackerSpecials = chain.pendingAttackData.slot?.specialRules || [];
  const remainingHits = stepResult.avoided > 0 ? chain.remainingHits.slice(stepResult.avoided) : chain.remainingHits;
  const cleavedHits = cleavedAfter(chain, stepResult, attackerSpecials);
  const counterAttackTriggered = hasCounterAttack(completedSteps);
  const availableSteps = offeredSteps(
    {
      target: chain.frozenTarget,
      isMelee: chain.isMelee,
      completedSteps,
      remaining: remainingHits.length,
      attackerSpecials,
      manualMod: chain.manualMod ?? 0,
    },
    rules,
  );
  return { ...chain, completedSteps, remainingHits, availableSteps, counterAttackTriggered, cleavedHits };
}

/** Roll a later step of the chain (Step Aside, Wall of Steel, or another dodge/parry). */
export function rollChainStep(chain: DefenceChainState, step: DefenceStep, unnaturalDosMode: UnnaturalDosMode) {
  const counter = chain.frozenTarget.counterAttackAvailable || false;
  return resolveDefenceStep(step, chain.remainingHits.length, counter, unnaturalDosMode);
}

/** Auto-complete: no hits left, or no more reactions to take. */
export function isChainDone(chain: DefenceChainState): boolean {
  return chain.remainingHits.length === 0 || chain.availableSteps.length === 0;
}

/** What `applyHits` is handed when the chain ends: Cleaving's blunted hits rejoin the volley, with a note. */
export function chainPayload(chain: DefenceChainState) {
  const { dodgeResult, parryResult } = toLegacyDefenceResults(chain.completedSteps);
  const cleaved = chain.cleavedHits || [];
  const baseNotes = (chain.pendingAttackData.specialNotes as string[] | undefined) || [];
  return {
    ...chain.pendingAttackData,
    hitsAfterDefence: [...cleaved, ...chain.remainingHits],
    ...(cleaved.length > 0
      ? {
          specialNotes: [...baseNotes, '⚔ Cleaving: ' + cleaved.length + ' parried hit(s) still bite for half damage.'],
        }
      : {}),
    fireMode: chain.pendingAttackData.effectiveMode,
    dodgeResult,
    parryResult,
    defenceChainSteps: chain.completedSteps,
  };
}

/** What `applyHits` is handed when nobody reacts: every hit, no chain. */
export function passThroughPayload(pa: PendingAttackData) {
  return {
    ...pa,
    hitsAfterDefence: [...pa.hits],
    fireMode: pa.effectiveMode,
    dodgeResult: null,
    parryResult: null,
    defenceChainSteps: [],
  };
}
